import { useState, useEffect } from "react";
import { MapPin, Star, Phone, Globe, Crown, Filter, Award, Check, Trophy, TrendingUp, Users, Target, CheckCircle2, ArrowRight, Zap, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { generateBreadcrumbSchema } from "@/utils/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import RatingBadge from "@/components/RatingBadge";
import AchievementBadge from "@/components/gamification/AchievementBadge";
import { useNavigate, Link } from "react-router-dom";
import { generateLocalBusinessSchema } from "@/utils/seo";
import { generateDirectoryTitle } from "@/utils/seoTitles";
import { supabase } from "@/integrations/supabase/client";
import certifiedBadge from "@/assets/ekselencia-certified-badge.png";
import { BrandedImage } from "@/components/images/BrandedImage";
import { DirectorySidebar, FilterState } from "@/components/directory/DirectorySidebar";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";

const ITEMS_PER_PAGE = 18;

const Directory = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState<string>("all");
  const [selectedCity, setSelectedCity] = useState<string>("all");
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    priceRange: [0, 100],
    distance: 50,
    features: [],
    categories: [],
    tags: [],
  });

  const countryToCityMapping: Record<string, string[]> = {
    "España": ["Alicante", "Madrid", "Barcelona", "Valencia", "Sevilla", "Cádiz", "Lugo", "Toledo", "León", "Avilés", "Teruel", "Aldaya", "Alcañiz", "Torrente", "Corvera de Asturias", "Castellón de la Plana", "Marbella"],
    "Estados Unidos": ["Miami", "Los Ángeles", "Salt Lake City", "Miami Beach", "Cypress Lake"],
    "Argentina": ["Buenos Aires"],
    "Chile": ["Santiago"],
    "Perú": ["Lima", "Chincha Baja", "Cuzco"],
    "Colombia": ["Medellín", "Barranquilla", "Santa Marta"],
    "México": ["Cancún"],
    "Venezuela": ["Caracas", "Porlamar", "Puerto La Cruz", "Cumaná"],
    "Panamá": ["Panamá", "Don Bosco", "Bethania", "Juan Díaz", "Parque Lefevre", "Bella Vista", "Pueblo Nuevo"],
    "Ecuador": ["Quito"],
    "Aruba": ["Noord"],
    "Trinidad y Tobago": ["Puerto España"],
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const batchSize = 1000;
        let offset = 0;
        const citiesAccumulator: string[] = [];

        while (true) {
          const { data, error } = await supabase
            .from("listings")
            .select("city")
            .eq("status", "active")
            .not("city", "is", null)
            .order("id", { ascending: true })
            .range(offset, offset + batchSize - 1);

          if (error) throw error;
          if (data && data.length > 0) {
            citiesAccumulator.push(...(data.map(d => d.city).filter(Boolean) as string[]));
          }
          if (!data || data.length < batchSize) break;
          offset += batchSize;
        }

        const extractedCountries = new Set<string>();
        citiesAccumulator.forEach(cityName => {
          const city = cityName?.trim() || "";
          for (const [country, cities] of Object.entries(countryToCityMapping)) {
            if (cities.some(c => city.includes(c))) {
              extractedCountries.add(country);
              break;
            }
          }
        });

        setCountries(Array.from(extractedCountries).sort());
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry !== "all") {
      const mappedCities = countryToCityMapping[selectedCountry] || [];
      setCities(mappedCities.sort());
    } else {
      setCities([]);
    }
    setSelectedCity("all");
  }, [selectedCountry]);

  useEffect(() => {
    fetchListings();
  }, [currentPage, selectedCountry, selectedCity, filters]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("listings")
        .select("*", { count: "exact" })
        .eq("status", "active")
        .order("is_premium", { ascending: false })
        .order("created_at", { ascending: false });

      if (selectedCountry !== "all") {
        const mappedCities = countryToCityMapping[selectedCountry] || [];
        if (mappedCities.length > 0) {
          const cityPatterns = mappedCities.map(city => `%${city}%`).join(",");
          query = query.or(mappedCities.map(city => `city.ilike.%${city}%`).join(","));
        }
      }

      if (selectedCity !== "all") {
        query = query.ilike("city", `%${selectedCity}%`);
      }

      if (filters.categories.length > 0) {
        query = query.or(filters.categories.map(cat => `type.ilike.%${cat}%`).join(","));
      }

      if (filters.tags.length > 0) {
        query = query.containedBy("tags", filters.tags);
      }

      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data, error, count } = await query.range(from, to);

      if (error) throw error;

      setListings(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error("Error fetching listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Directorio de Gimnasios y Centros Deportivos",
    description:
      "Encuentra y compara los mejores gimnasios, centros deportivos y boxes de CrossFit cerca de ti.",
    url: "https://ekselencia.com/directory",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: totalCount,
      itemListElement: listings.slice(0, 20).map((l, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://ekselencia.com/listing/${l.slug}`,
        name: l.name,
      })),
    },
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Inicio", url: "https://ekselencia.com/" },
    { name: "Directorio", url: "https://ekselencia.com/directory" },
  ]);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Directorio de Gimnasios y Centros Deportivos"
        description="Encuentra y compara los mejores gimnasios, centros deportivos y boxes de CrossFit cerca de ti. Más de 1000 centros verificados."
        keywords={["gimnasios", "centros deportivos", "crossfit", "fitness", "entrenamiento"]}
        structuredData={[collectionSchema, breadcrumbSchema]}
      />
      <Navigation />

      {/* Hero Section Ultra-Optimizado */}
      <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5" />
        
        <div className="container relative mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-6 py-3 rounded-full backdrop-blur-sm animate-fade-in">
              <Trophy className="w-5 h-5 text-primary animate-bounce" />
              <span className="font-semibold text-primary">+1,000 Gimnasios Verificados</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                Encuentra tu Gimnasio
              </span>
              <br />
              <span className="text-foreground">Perfecto Hoy Mismo</span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Compara <span className="font-bold text-primary">precios, instalaciones y ubicaciones</span> de más de 1,000 gimnasios y centros deportivos en un solo lugar
            </p>

            <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto pt-4">
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-1">+1K</div>
                <div className="text-sm text-muted-foreground">Gimnasios</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-1">20</div>
                <div className="text-sm text-muted-foreground">Países</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-1">100%</div>
                <div className="text-sm text-muted-foreground">Verificados</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button 
                size="lg" 
                className="text-lg px-8 py-7 bg-gradient-primary hover:opacity-90 transition-all hover:scale-105 shadow-2xl group"
                asChild
              >
                <a href="#gimnasios">
                  <MapPin className="w-5 h-5 mr-2" />
                  Ver Gimnasios Cerca
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-7 border-2 hover:bg-secondary/50 transition-all hover:scale-105 group"
                asChild
              >
                <Link to="/submit-listing">
                  <Trophy className="w-5 h-5 mr-2" />
                  Registrar mi Gimnasio
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-4 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>Comparación gratis</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>Información actualizada</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>Reseñas reales</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">Por Qué Usar Nuestro Directorio</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Ahorra Tiempo y Dinero
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Compara todos los gimnasios de tu zona en minutos, no en días
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Target,
                title: "Compara al Instante",
                description: "Ve precios, horarios e instalaciones de múltiples gimnasios lado a lado. Toma decisiones informadas."
              },
              {
                icon: MapPin,
                title: "Encuentra el Más Cercano",
                description: "Filtra por ubicación y distancia. Solo gimnasios que realmente están cerca de ti."
              },
              {
                icon: Star,
                title: "Lee Reseñas Reales",
                description: "Opiniones verificadas de usuarios reales. Sin sorpresas desagradables al inscribirte."
              }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-2 hover:border-primary/50 transition-all hover:shadow-xl group">
                <CardContent className="p-6 space-y-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Directory Content */}
      <div id="gimnasios" className="container mx-auto px-4 py-16">
        <Breadcrumbs items={[{ name: "Directorio", path: "/directory" }]} />

        <div className="mb-8">
          <DirectorySidebar 
            onFilterChange={(newFilters) => setFilters(newFilters)}
          />
        </div>

        <div className="mb-8 flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {totalCount} Gimnasios Encontrados
          </h2>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Cargando gimnasios...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => (
                <Card
                  key={listing.id}
                  className="cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1"
                  onClick={() => navigate(`/listing/${listing.slug}`)}
                >
                  <div className="relative overflow-hidden rounded-t-lg">
                    <BrandedImage
                      src={listing.thumbnail_url || listing.images?.[0]}
                      category={listing.type || 'gym'}
                      alt={listing.name}
                      aspectRatio="aspect-[4/3]"
                      logoSize="sm"
                    />
                    {listing.is_premium && (
                      <Badge className="absolute top-2 right-2 bg-gradient-primary z-20">
                        <Crown className="w-3 h-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </div>

                  <CardHeader>
                    <CardTitle className="line-clamp-2">{listing.name}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {listing.city}
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <div className="flex items-center justify-between">
                      <RatingBadge rating={listing.rating || 0} />
                      {listing.avg_monthly_price && (
                        <span className="font-bold text-primary">
                          €{listing.avg_monthly_price}/mes
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-4">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white space-y-6">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
              <Trophy className="w-5 h-5" />
              <span className="font-semibold">¿Eres Dueño de un Gimnasio?</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold">
              Atrae Más Clientes con Nosotros
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Miles de personas buscan gimnasios cada mes. Destaca tu centro y consigue nuevos miembros.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-7 mt-6 group"
              asChild
            >
              <Link to="/submit-listing">
                <Trophy className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                Registrar Mi Gimnasio Gratis
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Related Interlinks */}
      <div className="container mx-auto px-4 pb-8">
        <RelatedInterlinks 
          text="gimnasios centros fitness entrenamiento personal crossfit yoga pilates nutricionista fisioterapeuta entrenador" 
          maxLinks={6} 
        />
      </div>

      <Footer />
    </div>
  );
};

export default Directory;
